import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONDITION_OPTIONS = ["Additional documentation required", "Missouri or Illinois address verification required",
  "Insurance verification required", "Additional payment condition", "Different vehicle required",
  "Shorter rental period", "Local travel only", "No rideshare use", "Additional driver not approved",
  "Management call required before pickup"];

/**
 * Controlled "Approve & Book" shortcut — reserves the vehicle, creates the booking and sends the
 * customer their completion link, all from a single dialog without opening/expanding the request row.
 * Pass `request` as null to keep it closed, or {id, name, pickup_date, return_date} to open it.
 */
export default function QuickConvertDialog({ request, token, onClose, onDone }) {
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [vehOpts, setVehOpts] = useState(null);
  const [selVehicle, setSelVehicle] = useState("");
  const [conds, setConds] = useState([]);
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    if (!request) { setVehOpts(null); setSelVehicle(""); setConds([]); return; }
    axios.get(`${API}/reservations/${request.id}/vehicle-options`, H).then(({ data }) => {
      setVehOpts(data.options);
      const req = data.options.find((o) => o.requested);
      setSelVehicle(req?.available ? req.name : (data.options.find((o) => o.available)?.name || req?.name || ""));
    }).catch(() => setVehOpts([]));
    // eslint-disable-next-line
  }, [request?.id]);

  if (!request) return null;
  const selOpt = vehOpts?.find((o) => o.name === selVehicle);
  const needsOverride = !!(selOpt && !selOpt.available);

  const approveBook = async () => {
    if (needsOverride && !window.confirm(
      `OVERRIDE WARNING\n\n${selVehicle} shows BOOKED for ${request.pickup_date} → ${request.return_date}${selOpt?.conflict ? ` (${selOpt.conflict})` : ""}.\n\nBooking anyway will DOUBLE-BOOK this car. Only do this if you know the other rental ends early or the calendar is wrong.\n\nOverride and book anyway?`)) return;
    setBooking(true);
    try {
      const { data } = await axios.post(`${API}/reservations/${request.id}/approve-book`,
        { conditions: conds, vehicle: selVehicle, override: needsOverride }, H);
      toast.success(`Booked! ${data.vehicle} reserved ($${data.total_price}) — ${data.status}. $20 payment request ${data.reservation_payment_sms_sent ? "sent by SMS" : "link created (SMS not sent)"}.`);
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve & Book failed."); }
    finally { setBooking(false); }
  };

  return (
    <Dialog open={!!request} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="quick-convert-dialog" className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-[560px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><Zap size={18} className="text-[#F2B705]" /> Approve &amp; Book — {request.name}</DialogTitle>
          <DialogDescription className="text-white/45">{request.pickup_date} → {request.return_date}. This instantly reserves the vehicle, creates the booking, and texts + emails the customer a secure link to finish documents &amp; payment.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-1" data-testid={`qc-conditions-${request.id}`}>
            {CONDITION_OPTIONS.map((c) => (
              <label key={c} className="flex items-center gap-2 text-xs text-white/70 cursor-pointer">
                <input type="checkbox" checked={conds.includes(c)} onChange={(e) => setConds(e.target.checked ? [...conds, c] : conds.filter((x) => x !== c))} className="accent-[#F2B705]" />{c}
              </label>
            ))}
          </div>

          <div>
            <div className="text-xs text-white/50 mb-1.5">Vehicle for this booking — tap any car to swap {request.vehicle && <span className="text-white/35">(requested: {request.vehicle})</span>}</div>
            {!vehOpts ? <div className="text-white/40 text-xs flex items-center gap-2"><Loader2 size={11} className="animate-spin" /> Checking fleet availability…</div> : (
              <div className="grid sm:grid-cols-2 gap-1.5" data-testid={`qc-vehicle-swap-${request.id}`}>
                {vehOpts.map((o) => (
                  <button key={o.name} type="button" data-testid={`qc-veh-opt-${o.name.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => setSelVehicle(o.name)}
                    title={o.conflict || ""}
                    className={`text-left px-2.5 py-2 rounded-md border text-xs transition-colors ${selVehicle === o.name ? (o.available ? "border-[#F2B705] bg-[#F2B705]/10" : "border-red-500 bg-red-500/10") : "border-white/10 bg-black/30"} ${!o.available ? "opacity-70" : ""} hover:border-white/30`}>
                    <div className="font-semibold text-white truncate">{o.name}{o.requested ? " ★" : ""}</div>
                    <div className="flex justify-between mt-0.5">
                      <span className={o.available ? "text-green-400" : "text-red-300"}>{o.available ? "Available ✅" : "Booked ⛔"}</span>
                      <span className="text-white/50">${Math.round(o.total)}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {needsOverride && (
              <div data-testid="qc-override-warning" className="mt-2 bg-red-950/40 border border-red-500/40 rounded-md px-3 py-2 text-[11px] text-red-300">
                ⚠️ <b>OVERRIDE MODE</b> — {selVehicle} shows Booked{selOpt?.conflict ? ` (${selOpt.conflict})` : ""} for these dates. Booking will <b>double-book</b> this car. The override is logged with your name.
              </div>
            )}
          </div>

          <button data-testid="qc-approve-book-btn" onClick={approveBook} disabled={booking || !selVehicle}
            className={`w-full flex items-center justify-center gap-1.5 px-5 py-3 rounded-md font-bold text-sm disabled:opacity-50 ${needsOverride ? "bg-red-500 text-white" : "bg-[#F2B705] text-black hover:bg-white"} transition-colors`}>
            {booking ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} {needsOverride ? "Override & Book Anyway" : "Approve & Book — Reserve Vehicle Now"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
